import { useCallback, useEffect, useRef } from 'react';
import { hashEmail } from 'helpers/hashEmail';
import { getLeavingPopUpSteps } from 'helpers/leavingPopUpSteps';
import { getPreviousLocation } from 'helpers/navigationHistory';

import { AnalyticsManager, sessionId, SessionStorageManager } from '@atlas/core';

import {
  POSTHOG_CTA,
  POSTHOG_EVENTS,
  POSTHOG_SESSION_KEYS,
  SIGNUP_VIA_GOOGLE_STORAGE_KEY,
} from 'common/constants';

const POSTHOG_READY_POLL_INTERVAL = 300;
const POSTHOG_READY_MAX_ATTEMPTS = 20;

const useRegistrationPostHog = ({
  activeStep,
  cxFromParam,
  cxFromPath,
  isOAuthRegistration,
  postHog,
  stepsAmount,
}) => {
  const hasTransitionedRef = useRef(false);

  const authFlow =
    isOAuthRegistration || SessionStorageManager.getItem(SIGNUP_VIA_GOOGLE_STORAGE_KEY)
      ? 'google'
      : 'manual';

  const pageRefer =
    cxFromParam ?? cxFromPath ?? getPreviousLocation()?.pathname ?? window.location.pathname;

  const buildStepData = useCallback(
    step => {
      const { affid, affsource, btag, src } = AnalyticsManager.getAnalytics();

      return {
        affid: affid ?? null,
        affsource: affsource ?? null,
        auth_flow: authFlow,
        btag: btag ?? null,
        cta_clicked: null,
        email: null,
        email_domain_clicked: null,
        leaving_cta_clicked: null,
        leaving_pop_up_step: getLeavingPopUpSteps(),
        manual_closed_timestamp: null,
        page_refer: pageRefer,
        registration_step: step,
        session_id: sessionId,
        shown_timestamp: Date.now(),
        src: src ?? null,
        target_URL: null,
        total_registration_steps: stepsAmount,
      };
    },
    [authFlow, pageRefer, stepsAmount],
  );

  const concatStep = useCallback(
    step => {
      postHog.concat(POSTHOG_EVENTS.REGISTRATION, buildStepData(step));
    },
    [postHog, buildStepData],
  );

  useEffect(() => {
    if (!stepsAmount || hasTransitionedRef.current) return undefined;

    let attempts = 0;

    // Fires the registration event with registration_step: 0 once per session
    // (deduped by distinct_id via sessionStorage) when the form opens, then seeds
    // the current step buffer. Waits for PostHog to be ready (window.posthog) so
    // the step-0 send is not silently dropped before init() resolves, and runs the
    // discrete send before seeding so it does not clobber the current step buffer.
    const sendInitialStep = () => {
      if (!window.posthog) return false;

      if (!SessionStorageManager.getItem(POSTHOG_SESSION_KEYS.REGISTRATION_STEP_0_SENT)) {
        postHog.send(POSTHOG_EVENTS.REGISTRATION, buildStepData(0));
        SessionStorageManager.setItem(POSTHOG_SESSION_KEYS.REGISTRATION_STEP_0_SENT, true);
      }

      concatStep(activeStep + 1);
      return true;
    };

    if (sendInitialStep()) return undefined;

    const intervalId = setInterval(() => {
      attempts += 1;
      if (sendInitialStep() || attempts >= POSTHOG_READY_MAX_ATTEMPTS) {
        clearInterval(intervalId);
      }
    }, POSTHOG_READY_POLL_INTERVAL);

    return () => clearInterval(intervalId);
  }, [stepsAmount]);

  const handleNextStep = useCallback(
    (ctaClicked, skipConcat = false, email = null) => {
      hasTransitionedRef.current = true;
      if (ctaClicked) {
        postHog.send(POSTHOG_EVENTS.REGISTRATION, {
          auth_flow: authFlow,
          cta_clicked: ctaClicked,
          email: hashEmail(email),
        });
      }
      if (!skipConcat) {
        concatStep(activeStep + 2);
      }
    },
    [postHog, concatStep, activeStep, authFlow],
  );

  const handleLeave = useCallback(
    targetUrl => {
      postHog.send(POSTHOG_EVENTS.REGISTRATION, {
        auth_flow: authFlow,
        manual_closed_timestamp: Date.now(),
        target_URL: targetUrl ?? null,
      });
      SessionStorageManager.removeItem(SIGNUP_VIA_GOOGLE_STORAGE_KEY);
    },
    [postHog, authFlow],
  );

  const handleBackClick = useCallback(() => {
    postHog.send(POSTHOG_EVENTS.REGISTRATION, {
      auth_flow: authFlow,
      cta_clicked: POSTHOG_CTA.BACK_TO_PREV_STEP,
    });
    concatStep(activeStep);
  }, [postHog, concatStep, activeStep, authFlow]);

  const handleRegistrationSuccess = useCallback(
    ({ clientId, email, targetUrl }) => {
      postHog.send(POSTHOG_EVENTS.REGISTRATION, {
        auth_flow: authFlow,
        client_id: clientId,
        cta_clicked: POSTHOG_CTA.NEXT_STEP,
        email: hashEmail(email),
        target_URL: targetUrl ?? null,
      });
      SessionStorageManager.removeItem(SIGNUP_VIA_GOOGLE_STORAGE_KEY);
    },
    [postHog, authFlow],
  );

  return {
    handleBackClick,
    handleLeave,
    handleNextStep,
    handleRegistrationSuccess,
  };
};

export default useRegistrationPostHog;
